import { useState, useEffect, useCallback, useRef } from 'react'
import { ChatPromptService } from '../services/chatPrompts'
import { VideoService } from '../services/videos'
import { AuthService } from '../services/auth'
import type { ChatPromptWithVideo } from '../services/chatPrompts'
import type { VideoWithCategory } from '../services/videos'

interface ChatPromptFormData {
  video_id: string | null
  title: string
  system_prompt: string
  initial_message: string
  model: string
  temperature: number
  max_tokens: number
  is_active: boolean
  is_generic: boolean
}

const ChatPromptManager = () => {
  const [prompts, setPrompts] = useState<ChatPromptWithVideo[]>([])
  const [videos, setVideos] = useState<VideoWithCategory[]>([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingPrompt, setEditingPrompt] = useState<ChatPromptWithVideo | null>(null)
  const [formData, setFormData] = useState<ChatPromptFormData>({
    video_id: null,
    title: '',
    system_prompt: '',
    initial_message: '',
    model: 'gpt-4',
    temperature: 0.7,
    max_tokens: 1000,
    is_active: true,
    is_generic: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [existingGenericPrompt, setExistingGenericPrompt] = useState<ChatPromptWithVideo | null>(null)

  const loadPrompts = useCallback(async () => {
    try {
      setIsLoading(true)
      const promptsData = await ChatPromptService.getAllPrompts()
      setPrompts(promptsData)

      // Check for existing generic prompt
      const genericPrompt = promptsData.find(p => p.is_generic)
      setExistingGenericPrompt(genericPrompt || null)
    } catch (error) {
      console.error('Failed to load chat prompts:', error)
      setError('みらいワークの読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const loadVideos = useCallback(async () => {
    try {
      const videosData = await VideoService.getVideos({ limit: 1000 }) // Get all videos
      setVideos(videosData)
    } catch (error) {
      console.error('Failed to load videos:', error)
      setError('動画の読み込みに失敗しました')
    }
  }, [])

  useEffect(() => {
    loadPrompts()
    loadVideos()
  }, [loadPrompts, loadVideos])

  const resetForm = () => {
    setFormData({
      video_id: null,
      title: '',
      system_prompt: '',
      initial_message: '',
      model: 'gpt-4',
      temperature: 0.7,
      max_tokens: 1000,
      is_active: true,
      is_generic: false
    })
    setEditingPrompt(null)
    setError('')
  }

  const handleAddPrompt = () => {
    resetForm()
    setShowAddForm(true)
  }

  const handleEditPrompt = (prompt: ChatPromptWithVideo) => {
    setFormData({
      video_id: prompt.video_id || null,
      title: prompt.title,
      system_prompt: prompt.system_prompt,
      initial_message: prompt.initial_message || '',
      model: prompt.model,
      temperature: prompt.temperature,
      max_tokens: prompt.max_tokens,
      is_active: prompt.is_active,
      is_generic: prompt.is_generic || false
    })
    setEditingPrompt(prompt)
    setShowAddForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      if (editingPrompt) {
        // Update existing prompt
        await ChatPromptService.updatePrompt(editingPrompt.id, {
          video_id: formData.is_generic ? null : formData.video_id,
          title: formData.title,
          system_prompt: formData.system_prompt,
          initial_message: formData.initial_message || null,
          model: formData.model,
          temperature: formData.temperature,
          max_tokens: formData.max_tokens,
          is_active: formData.is_active,
          is_generic: formData.is_generic
        })

        await AuthService.logAdminAction('update_chat_prompt', 'chat_prompt', editingPrompt.id, {
          title: formData.title,
          video_id: formData.is_generic ? null : formData.video_id,
          is_generic: formData.is_generic
        })

        // Update local state directly to avoid full reload (which resets scroll position)
        const editedId = editingPrompt.id
        setPrompts(prev => prev.map(p =>
          p.id === editedId
            ? {
                ...p,
                video_id: formData.is_generic ? null : formData.video_id,
                title: formData.title,
                system_prompt: formData.system_prompt,
                initial_message: formData.initial_message || null,
                model: formData.model,
                temperature: formData.temperature,
                max_tokens: formData.max_tokens,
                is_active: formData.is_active,
                is_generic: formData.is_generic,
              }
            : p
        ))
        setShowAddForm(false)
        resetForm()
        setTimeout(() => {
          itemRefs.current[editedId]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 0)
        return
      } else {
        // Create new prompt
        const promptData = {
          video_id: formData.is_generic ? null : formData.video_id,
          title: formData.title,
          system_prompt: formData.system_prompt,
          initial_message: formData.initial_message || null,
          model: formData.model,
          temperature: formData.temperature,
          max_tokens: formData.max_tokens,
          is_active: formData.is_active,
          is_generic: formData.is_generic
        }
        
        const newPrompt = await ChatPromptService.createPrompt(promptData)
        
        await AuthService.logAdminAction('create_chat_prompt', 'chat_prompt', newPrompt.id, {
          title: formData.title,
          video_id: formData.video_id
        })
      }

      setShowAddForm(false)
      resetForm()
      await loadPrompts()
    } catch (err: unknown) {
      console.error('Failed to save chat prompt:', err)
      const errorMessage = err instanceof Error ? err.message : String(err)
      setError(errorMessage || 'みらいワークの保存に失敗しました')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeletePrompt = async (prompt: ChatPromptWithVideo) => {
    if (confirm(`「${prompt.title}」を削除してもよろしいですか？`)) {
      try {
        setIsLoading(true)
        await ChatPromptService.deletePrompt(prompt.id)
        
        await AuthService.logAdminAction('delete_chat_prompt', 'chat_prompt', prompt.id, {
          title: prompt.title,
          video_id: prompt.video_id
        })
        
        await loadPrompts()
      } catch (error: unknown) {
        console.error('Failed to delete chat prompt:', error)
        const errorMessage = error instanceof Error ? error.message : String(error)
        setError(errorMessage || 'みらいワークの削除に失敗しました')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleToggleActive = async (prompt: ChatPromptWithVideo) => {
    try {
      setIsLoading(true)
      await ChatPromptService.toggleActive(prompt.id)
      
      await AuthService.logAdminAction('toggle_chat_prompt_active', 'chat_prompt', prompt.id, {
        title: prompt.title,
        is_active: !prompt.is_active
      })
      
      await loadPrompts()
    } catch (error: unknown) {
      console.error('Failed to toggle chat prompt active status:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || 'みらいワークの状態変更に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const availableModels = ChatPromptService.getAvailableModels()
  const usedVideoIds = prompts.map(p => p.video_id).filter(Boolean)
  const availableVideos = videos.filter(video => !usedVideoIds.includes(video.id) || editingPrompt && editingPrompt.video_id === video.id)

  const renderForm = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-blue-200">
      <h3 className="text-base font-medium text-gray-900 mb-4">
        {editingPrompt ? 'みらいワークを編集' : '新しいみらいワークを追加'}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Generic Prompt Toggle */}
        <div className="border-b border-gray-200 pb-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="is_generic"
              checked={formData.is_generic}
              onChange={(e) => {
                const isGeneric = e.target.checked
                setFormData({
                  ...formData,
                  is_generic: isGeneric,
                  video_id: isGeneric ? null : formData.video_id
                })
              }}
              disabled={
                Boolean(
                  (!editingPrompt && formData.is_generic && existingGenericPrompt) ||
                  (editingPrompt && !editingPrompt.is_generic && formData.is_generic && existingGenericPrompt)
                )
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="is_generic" className="text-sm font-medium text-gray-700">
              汎用チャットプロンプト（特定の動画に紐づかない全般的なAIアシスタント）
            </label>
          </div>
          {formData.is_generic && (
            <p className="text-sm text-amber-600 mt-2 bg-amber-50 p-2 rounded">
              ⚠️ 汎用チャットプロンプトは1つだけ作成できます。これは動画一覧の右側に表示される全般的なAI相談機能に使用されます。
            </p>
          )}
          {!formData.is_generic && existingGenericPrompt && !editingPrompt && (
            <p className="text-sm text-blue-600 mt-2 bg-blue-50 p-2 rounded">
              💡 汎用チャットプロンプトは既に存在します。新しい汎用プロンプトを作成するには、既存のものを編集してください。
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {!formData.is_generic && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                対象動画 *
              </label>
              <select
                required={!formData.is_generic}
                value={formData.video_id || ''}
                onChange={(e) => setFormData({ ...formData, video_id: e.target.value || null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">動画を選択してください</option>
                {availableVideos.map((video) => (
                  <option key={video.id} value={video.id}>
                    {video.title} ({video.category})
                  </option>
                ))}
              </select>
            </div>
          )}
          {formData.is_generic && (
            <div>
              <div className="p-4 bg-gray-50 rounded-md border-2 border-dashed border-gray-300">
                <div className="text-center">
                  <div className="text-2xl text-gray-400 mb-2">🌐</div>
                  <p className="text-sm text-gray-600">汎用チャットプロンプト</p>
                  <p className="text-xs text-gray-500 mt-1">特定の動画に紐づきません</p>
                </div>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              みらいワークタイトル *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="例: はじめての起業アドバイザー"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            システムプロンプト *
          </label>
          <textarea
            required
            value={formData.system_prompt}
            onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="AIの役割と行動指針を詳細に記述してください..."
          />
          <p className="text-xs text-gray-500 mt-1">
            AIがどのような役割を果たし、どのように応答するかを明確に指示してください
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            初期メッセージ
          </label>
          <textarea
            value={formData.initial_message}
            onChange={(e) => setFormData({ ...formData, initial_message: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="例: こんにちは！このテーマについて一緒に考えてみましょう。何か気になることはありますか？"
          />
          <p className="text-xs text-gray-500 mt-1">
            チャット開始時にユーザーに表示されるウェルカムメッセージです。空白の場合はデフォルトメッセージが使用されます。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              モデル
            </label>
            <select
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableModels.map((model) => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Temperature (0.0 - 2.0)
            </label>
            <input
              type="number"
              min="0"
              max="2"
              step="0.1"
              value={formData.temperature}
              onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              低い値ほど一貫性が高く、高い値ほど創造的
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              最大トークン数
            </label>
            <input
              type="number"
              min="100"
              max="8192"
              value={formData.max_tokens}
              onChange={(e) => setFormData({ ...formData, max_tokens: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
            アクティブ（ユーザーが利用可能）
          </label>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={resetForm}
            disabled={isSubmitting}
            className="bg-gray-300 hover:bg-gray-400 disabled:opacity-50 text-gray-700 px-4 py-2 rounded-md text-sm font-medium"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={
              isSubmitting ||
              (!editingPrompt && formData.is_generic && Boolean(existingGenericPrompt)) ||
              (!formData.is_generic && !formData.video_id) ||
              !formData.title.trim() ||
              !formData.system_prompt.trim()
            }
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            {isSubmitting ? '保存中...' : editingPrompt ? '更新' : '作成'}
          </button>
        </div>
      </form>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">みらいワーク管理</h2>
          <p className="text-sm text-gray-600">各動画に対応するみらいワークの作成・編集・管理ができます</p>
        </div>
        <button
          onClick={handleAddPrompt}
          disabled={availableVideos.length === 0}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          みらいワークを追加
        </button>
      </div>


      {/* Add Form (shown at top only when not editing an existing item) */}
      {showAddForm && !editingPrompt && renderForm()}

      {/* Prompts List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-medium text-gray-900">
            みらいワーク一覧 ({prompts.length}件)
          </h3>
        </div>
        
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">読み込み中...</p>
          </div>
        ) : prompts.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {prompts.map((prompt) => (
              <div key={prompt.id} ref={el => { itemRefs.current[prompt.id] = el }}>
                {editingPrompt?.id === prompt.id && showAddForm ? (
                  renderForm()
                ) : (
                  <div className="p-6 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="flex items-center space-x-2">
                            {prompt.is_generic && (
                              <span className="text-lg">🌐</span>
                            )}
                            <h4 className="text-base font-medium text-gray-900">
                              {prompt.title}
                            </h4>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            prompt.is_generic
                              ? 'bg-purple-100 text-purple-800 font-medium'
                              : 'text-gray-500 bg-gray-100'
                          }`}>
                            {prompt.is_generic ? '汎用チャット' : (prompt.video?.title || '動画未設定')}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            prompt.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {prompt.is_active ? 'アクティブ' : '非アクティブ'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {prompt.system_prompt}
                        </p>
                        {prompt.initial_message && (
                          <div className="mb-2">
                            <p className="text-xs text-gray-500 mb-1">初期メッセージ:</p>
                            <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded text-sm line-clamp-2">
                              {prompt.initial_message}
                            </p>
                          </div>
                        )}
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>モデル: {prompt.model}</span>
                          <span>Temperature: {prompt.temperature}</span>
                          <span>最大トークン: {prompt.max_tokens}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleToggleActive(prompt)}
                          className={`px-3 py-1 rounded text-sm font-medium ${
                            prompt.is_active
                              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        >
                          {prompt.is_active ? '非アクティブにする' : 'アクティブにする'}
                        </button>
                        <button
                          onClick={() => handleEditPrompt(prompt)}
                          className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                        >
                          編集
                        </button>
                        <button
                          onClick={() => handleDeletePrompt(prompt)}
                          className="text-red-600 hover:text-red-500 text-sm font-medium"
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-gray-500">みらいワークがありません</p>
            {availableVideos.length > 0 ? (
              <button
                onClick={handleAddPrompt}
                className="mt-2 text-blue-600 hover:text-blue-500 text-sm font-medium"
              >
                最初のみらいワークを追加
              </button>
            ) : (
              <p className="mt-2 text-sm text-gray-400">
                すべての動画にみらいワークが設定済みです
              </p>
            )}
          </div>
        )}
      </div>

      {error && !showAddForm && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
    </div>
  )
}

export default ChatPromptManager